import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAssetAgeAnalysis } from '@/hooks/use-asset-management-widgets';
import { Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';

const AGE_COLORS = {
  '0-1': '#86efac', // green-300
  '1-3': '#93c5fd', // blue-300
  '3-5': '#fcd34d', // amber-300
  '5-10': '#fdba74', // orange-300
  '10+': '#fca5a5', // red-300
};

export function AssetAgeAnalysisWidget() {
  const { data, isLoading, error } = useAssetAgeAnalysis();

  if (isLoading) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Asset Age Analysis</CardTitle>
              <CardDescription>Age distribution of assets</CardDescription>
            </div>
            <div className="p-2 bg-cyan-50 dark:bg-cyan-950 rounded-lg">
              <Calendar className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Handle error state (API failure)
  if (error) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Asset Age Analysis</CardTitle>
              <CardDescription>Age distribution of assets</CardDescription>
            </div>
            <div className="p-2 bg-cyan-50 dark:bg-cyan-950 rounded-lg">
              <Calendar className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex h-[300px] items-center justify-center text-muted-foreground">
            Unable to load age analysis data
          </div>
        </CardContent>
      </Card>
    );
  }

  // Handle empty data state (no assets in database)
  if (!data?.ageBuckets?.length) {
    return (
      <Card className="relative overflow-hidden shadow-sm bg-white dark:bg-black h-full flex flex-col">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1.5">
              <CardTitle className="text-base font-semibold tracking-tight">Asset Age Analysis</CardTitle>
              <CardDescription>Age distribution of assets</CardDescription>
            </div>
            <div className="p-2 bg-cyan-50 dark:bg-cyan-950 rounded-lg">
              <Calendar className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="flex-1">
          <div className="flex flex-col h-[300px] items-center justify-center text-center">
            <Calendar className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No age analysis data available
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const getColorForLabel = (label: string): string => {
    // Map label to color key
    if (label.includes('0-1') || label.includes('< 1')) return AGE_COLORS['0-1'];
    if (label.includes('1-3')) return AGE_COLORS['1-3'];
    if (label.includes('3-5')) return AGE_COLORS['3-5'];
    if (label.includes('5-10')) return AGE_COLORS['5-10'];
    if (label.includes('10+') || label.includes('> 10')) return AGE_COLORS['10+'];
    return '#93c5fd'; // default blue
  };

  return (
    <Card className={cn('relative overflow-hidden shadow-sm hover:shadow-md transition-all duration-300 bg-white dark:bg-black h-full flex flex-col')}>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="space-y-1.5">
            <CardTitle className="text-base font-semibold tracking-tight">Asset Age Analysis</CardTitle>
            <CardDescription>Age distribution of assets</CardDescription>
          </div>
          <div className="p-2 bg-cyan-50 dark:bg-cyan-950 rounded-lg">
            <Calendar className="h-5 w-5 text-cyan-600 dark:text-cyan-400" />
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1">
        {/* Summary Stats */}
        <div className="mb-6 grid grid-cols-2 gap-4">
          <div className="text-center p-4 rounded-lg border bg-card">
            <div className="text-3xl font-bold text-cyan-600 dark:text-cyan-400">{data.averageAge.toFixed(1)}</div>
            <div className="text-sm text-muted-foreground mt-1">Average Age (years)</div>
          </div>
          <div className="text-center p-4 rounded-lg border bg-card">
            <div className="text-3xl font-bold">{data.totalAssets.toLocaleString()}</div>
            <div className="text-sm text-muted-foreground mt-1">Total Assets</div>
          </div>
        </div>

        {/* Age Range Cards */}
        <div className="space-y-3">
          {data.ageBuckets.map((bucket: any) => {
            const color = getColorForLabel(bucket.label);
            return (
              <div
                key={bucket.label}
                className="p-4 rounded-lg border bg-card hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 flex-1">
                    <div
                      className="w-4 h-4 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: color }}
                    />
                    <div className="flex-1">
                      <div className="font-semibold text-sm">{bucket.label}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {bucket.percentage.toFixed(1)}% of total assets
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold" style={{ color }}>
                      {bucket.count}
                    </div>
                    <div className="text-xs text-muted-foreground">assets</div>
                  </div>
                </div>
                
                {/* Progress bar */}
                <div className="mt-3 w-full bg-muted rounded-full h-2 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{ 
                      width: `${bucket.percentage}%`,
                      backgroundColor: color
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
